import { Layout } from "@/components/layout/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/hooks/useAuth";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Save } from "lucide-react";

export function Profile() {
  const { user, loading, logout, updateProfile } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    display_name: "",
    country: "",
    biography: "",
    preferred_variant: "zimbabwe-standard",
  });
  const [busy, setBusy] = useState(false);

  if (loading) {
    return (
      <Layout>
        <section className="max-w-3xl mx-auto px-4 py-16 text-center text-muted-foreground">
          Loading…
        </section>
      </Layout>
    );
  }
  if (!user) {
    navigate("/login");
    return null;
  }

  // hydrate form lazily
  if (!form._loaded) {
    setForm({
      display_name: user.display_name || "",
      country: user.country || "",
      biography: user.biography || "",
      preferred_variant: user.preferred_variant || "zimbabwe-standard",
      _loaded: true,
    });
  }

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await updateProfile({
        display_name: form.display_name,
        country: form.country,
        biography: form.biography,
        preferred_variant: form.preferred_variant,
      });
      toast.success("Profile saved.");
    } catch (err) {
      toast.error("Could not save profile.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Layout>
      <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <p className="text-xs uppercase tracking-widest text-muted-foreground">Profile</p>
            <h1 className="font-display text-4xl mt-1">{user.display_name || user.username}</h1>
            <p className="text-muted-foreground text-sm mt-1">
              @{user.username} · joined {new Date(user.created_at).toLocaleDateString()}
            </p>
          </div>
          <div className="flex gap-2">
            <Badge variant="outline" className="rounded-full uppercase tracking-widest text-[10px]">
              {user.membership === "premium" ? "Play Tsoro Premium" : "Free account"}
            </Badge>
            <Button
              data-testid="logout-btn"
              variant="outline"
              className="rounded-full"
              onClick={async () => {
                await logout();
                navigate("/");
              }}
            >
              <LogOut className="h-4 w-4 mr-2" /> Sign out
            </Button>
          </div>
        </div>

        <div className="mt-8 grid sm:grid-cols-4 gap-3">
          <Stat label="Games played" value={user.stats?.played || 0} />
          <Stat label="Wins" value={user.stats?.wins || 0} />
          <Stat label="Losses" value={user.stats?.losses || 0} />
          <Stat label="Largest capture" value={user.stats?.largest_capture || 0} />
        </div>

        <Card className="mt-8 p-6">
          <p className="font-display text-xl">Edit details</p>
          <form onSubmit={save} className="mt-4 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="display_name">Display name</Label>
              <Input
                id="display_name"
                value={form.display_name}
                onChange={(e) => setForm({ ...form, display_name: e.target.value })}
                maxLength={40}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="country">Country</Label>
              <Input
                id="country"
                value={form.country}
                onChange={(e) => setForm({ ...form, country: e.target.value })}
                maxLength={60}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="biography">Biography</Label>
              <Textarea
                id="biography"
                value={form.biography}
                onChange={(e) => setForm({ ...form, biography: e.target.value })}
                maxLength={400}
                rows={3}
                placeholder="A short note about your Tsoro story."
              />
            </div>
            <Button type="submit" disabled={busy} className="rounded-full">
              <Save className="h-4 w-4 mr-2" /> {busy ? "Saving…" : "Save changes"}
            </Button>
          </form>
        </Card>
      </section>
    </Layout>
  );
}

function Stat({ label, value }) {
  return (
    <Card className="p-4">
      <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</p>
      <p className="font-mono font-semibold text-2xl mt-1">{value}</p>
    </Card>
  );
}
